import React, { useLayoutEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { createEmployee, updateEmployee } from '../services/api';
import { Employee, useEmployeeStoreBase, updateEmployeeInLists } from '../stores/employeeStore';
import { removeListItem } from '../utils/pendingItem';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';
import { dateInputValue, ymd } from '../utils/attendance';
import { useUnsavedChangesWarning } from '@redpanda/utils';

interface EmployeeDialogProps {
  employee?: Employee;
  onClose: () => void;
  /** API 成功後（重新整理方案人數） */
  onSaved?: () => void;
}

interface FormData {
  name: string;
  employeeNumber: string;
  department: string;
  position: string;
  email: string;
  phone: string;
  idNumber: string;
  hireDate: string;
  resignationDate: string;
  status: string;
}

const emptyForm = (): FormData => ({
  name: '', employeeNumber: '', department: '', position: '', email: '', phone: '', idNumber: '',
  hireDate: ymd(new Date()), resignationDate: '', status: 'ACTIVE',
});

/** 新增／編輯員工：樂觀更新＋立即關閉（§3-17），失敗回滾 */
const EmployeeDialog: React.FC<EmployeeDialogProps> = ({ employee, onClose, onSaved }) => {
  const { t } = useTranslation(['pages', 'translation']);
  const isEditMode = !!employee;
  const { register, handleSubmit, formState: { errors, isDirty }, reset, setValue, watch } = useForm<FormData>({ defaultValues: emptyForm() });

  // 初始值在繪製前帶入，編輯時不會先閃一格空白欄位（R31）
  useLayoutEffect(() => {
    reset(employee ? {
      name: employee.name,
      employeeNumber: employee.employeeNumber || '',
      department: employee.department || '',
      position: employee.position || '',
      email: employee.email || '',
      phone: employee.phone || '',
      idNumber: employee.idNumber || '',
      hireDate: dateInputValue(employee.hireDate),
      resignationDate: dateInputValue(employee.resignationDate),
      status: employee.status || 'ACTIVE',
    } : emptyForm());
  }, [employee, reset]);

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty, onClose);

  const onSubmit = (data: FormData) => {
    const payload = {
      ...data,
      name: data.name.trim(),
      hireDate: `${data.hireDate}T00:00:00.000Z`,
      resignationDate: data.resignationDate ? `${data.resignationDate}T00:00:00.000Z` : null,
    };
    onClose();

    if (employee) {
      const original = { ...employee };
      updateEmployeeInLists(employee.id, { ...payload, resignationDate: payload.resignationDate ?? undefined });
      updateEmployee(employee.id, payload)
        .then(response => {
          updateEmployeeInLists(employee.id, response.data);
          toast.success(t('pages:employees.edit.success'));
          notifyDataChanged('employees');
        })
        .catch(error => {
          updateEmployeeInLists(employee.id, original);
          toast.error(apiErrorMessage(error, 'translation:common.messages.updateFailed'));
        });
      return;
    }

    const tempId = `temp-${Date.now()}`;
    useEmployeeStoreBase.getState().addItem({ ...payload, resignationDate: undefined, id: tempId, createdAt: new Date().toISOString() });
    createEmployee(payload)
      .then(response => {
        useEmployeeStoreBase.getState().updateItem(tempId, response.data);
        toast.success(t('pages:employees.add.success'));
        notifyDataChanged('employees');
        onSaved?.();
      })
      .catch(error => {
        removeListItem(useEmployeeStoreBase, tempId);
        toast.error(apiErrorMessage(error, 'translation:common.messages.createFailed'));
      });
  };

  const label = (key: string) => t(`translation:common.forms.labels.${key}`);

  return (
    <>
      <DialogShell onClose={handleClose} className="max-w-2xl">
        <DialogHeader
          title={isEditMode ? t('pages:employees.edit.title') : t('pages:employees.add.title')}
          onClose={handleClose}
          closeLabel={t('translation:common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <FormFieldWithError label={label('name')} required error={errors.name?.message} htmlFor="name">
              <Input
                id="name"
                autoFocus
                {...register('name', {
                  validate: value => !!value.trim() || t('translation:common.forms.validation.required'),
                  maxLength: { value: 100, message: t('translation:common.forms.validation.maxLength', { length: 100 }) },
                })}
                variant={errors.name ? 'error' : 'default'}
              />
            </FormFieldWithError>
            <FormFieldWithError label={label('employeeNumber')} helpText={isEditMode ? undefined : t('pages:employees.form.numberHint')} htmlFor="employeeNumber">
              <Input id="employeeNumber" {...register('employeeNumber')} />
            </FormFieldWithError>
            <FormFieldWithError label={label('department')} htmlFor="department">
              <Input id="department" {...register('department')} />
            </FormFieldWithError>
            <FormFieldWithError label={label('position')} htmlFor="position">
              <Input id="position" {...register('position')} />
            </FormFieldWithError>
            <FormFieldWithError label={label('email')} error={errors.email?.message} htmlFor="email">
              <Input
                id="email"
                type="email"
                {...register('email', {
                  pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: t('translation:common.forms.validation.invalidEmail') },
                })}
                variant={errors.email ? 'error' : 'default'}
              />
            </FormFieldWithError>
            <FormFieldWithError label={label('phone')} htmlFor="phone">
              <Input id="phone" type="tel" {...register('phone')} />
            </FormFieldWithError>
            <FormFieldWithError label={label('idNumber')} htmlFor="idNumber">
              <Input id="idNumber" {...register('idNumber')} />
            </FormFieldWithError>
            <FormFieldWithError label={label('status')} required htmlFor="status">
              <CustomSelect
                id="status"
                value={watch('status')}
                onChange={value => setValue('status', value, { shouldDirty: true })}
                options={['ACTIVE', 'ON_LEAVE', 'RESIGNED'].map(value => ({ value, label: t(`translation:employeeStatus.${value}`) }))}
              />
            </FormFieldWithError>
            <FormFieldWithError label={label('hireDate')} required error={errors.hireDate?.message} htmlFor="hireDate">
              <Input
                id="hireDate"
                type="date"
                {...register('hireDate', { required: t('translation:common.forms.validation.required') })}
                variant={errors.hireDate ? 'error' : 'default'}
              />
            </FormFieldWithError>
            <FormFieldWithError label={label('resignationDate')} error={errors.resignationDate?.message} htmlFor="resignationDate">
              <Input
                id="resignationDate"
                type="date"
                {...register('resignationDate', {
                  validate: value => !value || value >= watch('hireDate') || t('pages:employees.form.resignationBeforeHire'),
                })}
                variant={errors.resignationDate ? 'error' : 'default'}
              />
            </FormFieldWithError>
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} className={dialogButtonClass.cancel}>
              {t('translation:common.actions.cancel')}
            </button>
            <button type="submit" className={dialogButtonClass.primary}>
              {isEditMode ? t('translation:common.actions.save') : t('translation:common.actions.create')}
            </button>
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('translation:components.modal.unsavedChanges.title')}
        message={t('translation:components.modal.unsavedChanges.message')}
        confirmText={t('translation:components.modal.unsavedChanges.confirmButton')}
        cancelText={t('translation:components.modal.unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default EmployeeDialog;
